import { Box, Container, Grid, Typography } from "@mui/material"
import { useApi } from "auth/useApi3"
import ClpTable from "components/ClpTable"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import { allRoutes } from "generated/routes"
import { useContext, useEffect } from "react"
import { useHistory } from "react-router-dom"
import { pdf } from "@react-pdf/renderer"
import { CertificateDocument } from "shared/learning/components/CertificateDocument"

import {
  DatePickerColumnFilter,
  MultiSelectFilter,
  NumberRangeColumnFilter
} from "components/ClpTable/ClpTableFilterTypes"
import { StyledBoldTypography } from "components/ClpTable/components/typography/typography.styled"
import { apiEndpoints } from "generated/apiEndpoints"
import moment from "moment"
import { StyledTableContainer } from "shared/styled/Global"
import { useTranslation } from "react-i18next"
import { TableButtonTypeEnum } from "components/ClpTable/components/actions/TableButton/tableButtonProps.interface"
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import { getExaminationNameTranslation } from "../exams/pages/details/components/helpers/AttendeeStatus"

const AdminLearningResults = () => {
  const routerHistory = useHistory()
  const loadingContext = useContext(GlobalLoaderContext)
  const [examResultsResult, examResultsFuncs] = useApi()
  const { t } = useTranslation()

  useEffect(() => {
    examResultsFuncs.get(apiEndpoints.learningadmin.getexamresultslonglist)
  }, [examResultsFuncs])

  useEffect(() => {
    loadingContext.setLoading(examResultsResult.status === 1)
  })

  const columns = [
    {
      accessorKey: "userFullname",
      header: t("global.table.name", "Name"),
      cell: (cell) => {
        return <StyledBoldTypography>{cell.row.original.userFullname}</StyledBoldTypography>
      }
    },
    {
      accessorKey: "userEmail",
      header: t("global.table.email", "Email")
    },
    {
      accessorFn: (row) => {
        return getExaminationNameTranslation(row?.examinationName, t)
      },
      header: t("learning.titles.training", "Training"),
      filterFn: "arrIncludesSome",
      meta: {
        filterHeader: t("learning.titles.training", "Training"),
        mainFilter: true,
        filter: MultiSelectFilter
      }
    },
    {
      accessorKey: "userManagerName",
      header: t("global.manager", "Manager"),
      filterFn: "arrIncludesSome",
      meta: {
        mainFilter: true,
        filter: MultiSelectFilter
      }
    },
    {
      accessorKey: "userCompanies",
      header: t("global.company", "Company"),
      filterFn: "arrIncludesSome",
      meta: {
        mainFilter: true,
        filter: MultiSelectFilter
      }
    },
    {
      accessorKey: "examinationScore",
      header: t("global.result", "Result"),
      meta: {
        mainFilter: true,
        filter: NumberRangeColumnFilter
      },
      filter: "between"
    },
    {
      accessorFn: (row) => {
        return row.examinationDate && moment(row.examinationDate).format("YYYY-MM-DD")
      },
      header: t("learning.titles.learning_results", "Educational results"),
      meta: {
        filter: DatePickerColumnFilter
      }
    },
    {
      header: t("learning.titles.passed", "Passed"),
      accessorFn: (row) => {
        return row.passed ? t("global.yes", "Yes") : t("global.no", "No")
      }
    },
    {
      accessorKey: "download",
      header: "",
      align: "right",
      cell: ({ cell }) => {
        return (
          cell.row.original.passed === true && (
            <TableButton
              type={TableButtonTypeEnum.Download}
              handleOnClick={async (e) => {
                if (e && e.stopPropagation) {
                  e.stopPropagation()
                }

                let blobPDF = await pdf(
                  <CertificateDocument
                    t={t}
                    userFullName={cell.row.original.userFullname}
                    examinationName={cell.row.original.examinationName}
                    completedDate={cell.row.original.examinationDate}
                  />
                ).toBlob()

                const link = document.createElement("a")
                link.href = window.URL.createObjectURL(blobPDF)
                link.download = `${t(
                  "learning.titles.certificate_of_education",
                  "Certificate of education"
                )}-${cell.row.original.userFullname}.pdf`
                link.click()
                window.URL.revokeObjectURL(link.href)
              }}
            />
          )
        )
      }
    }
  ]

  return (
    <div>
      <Container>
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h1" gutterBottom>
              {t("learning.titles.learning_result", "Educational results")}
            </Typography>
            <Typography variant="paragraphLarge">
              {t(
                "learning.titles.see_results_and_download",
                "Here you can see results and download training certificates."
              )}
            </Typography>
          </Box>
        </Grid>
      </Container>

      <StyledTableContainer>
        <ClpTable
          rows={examResultsResult.status === 2 && examResultsResult.data}
          loadingState={examResultsResult.status}
          columns={columns}
          onRowClick={(row) => routerHistory.push(allRoutes.LearningAdminResultsDetails(row.original.id))}
        />
      </StyledTableContainer>
    </div>
  )
}

export default AdminLearningResults
